import { useEffect, useRef, useState } from "react";

const LIVE_BAR_COUNT = 40;
const STATIC_BAR_COUNT = 60;

export const dimUnplayedBars = (
  bars: number[] | null,
  durationMs: number,
  progressMs: number,
  unplayedScale = 0.4,
): number[] | null => {
  if (!bars || durationMs <= 0) return bars;
  const ratio = progressMs / durationMs;
  return bars.map((v, i) => (i / bars.length <= ratio ? v : v * unplayedScale));
};

export const formatAudioDuration = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const useAudioBlob = (url: string | null | undefined): Blob | null => {
  const [blob, setBlob] = useState<Blob | null>(null);

  useEffect(() => {
    if (!url) {
      setBlob(null);
      return;
    }
    let cancelled = false;
    fetch(url)
      .then((r) => r.blob())
      .then((b) => {
        if (!cancelled) setBlob(b);
      })
      .catch(() => {
        if (!cancelled) setBlob(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return blob;
};

export const useLiveAudioWaveform = (
  stream: MediaStream | null,
): number[] => {
  const [bars, setBars] = useState<number[]>(() =>
    new Array<number>(LIVE_BAR_COUNT).fill(0),
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream) {
      setBars(new Array<number>(LIVE_BAR_COUNT).fill(0));
      return;
    }

    let cancelled = false;
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    const smoothed = new Array<number>(LIVE_BAR_COUNT).fill(0);
    const PER_FRAME_LERP = 0.35;

    const minBin = 1;
    const maxBin = Math.min(buffer.length, 96);
    const barRanges = new Array<[number, number]>(LIVE_BAR_COUNT);
    for (let i = 0; i < LIVE_BAR_COUNT; i += 1) {
      const t0 = i / LIVE_BAR_COUNT;
      const t1 = (i + 1) / LIVE_BAR_COUNT;
      const a = Math.floor(minBin * Math.pow(maxBin / minBin, t0));
      const b = Math.max(a + 1, Math.floor(minBin * Math.pow(maxBin / minBin, t1)));
      barRanges[i] = [a, b];
    }

    const tick = () => {
      if (cancelled) return;
      analyser.getByteFrequencyData(buffer);

      const next = new Array<number>(LIVE_BAR_COUNT);
      for (let i = 0; i < LIVE_BAR_COUNT; i += 1) {
        const [start, end] = barRanges[i];
        let sum = 0;
        for (let j = start; j < end; j += 1) {
          sum += buffer[j];
        }
        const raw = sum / (end - start) / 255;
        const shaped = Math.sqrt(raw);
        smoothed[i] = smoothed[i] * (1 - PER_FRAME_LERP) + shaped * PER_FRAME_LERP;
        next[i] = smoothed[i];
      }
      setBars(next);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      source.disconnect();
      analyser.disconnect();
      void audioCtx.close();
    };
  }, [stream]);

  return bars;
};

export const useStaticAudioWaveform = (
  blob: Blob | null,
): number[] | null => {
  const [bars, setBars] = useState<number[] | null>(null);

  useEffect(() => {
    if (!blob) {
      setBars(null);
      return;
    }

    let cancelled = false;
    // Fresh AudioContext per decode so close() releases the buffer.
    const audioCtx = new AudioContext();

    blob
      .arrayBuffer()
      .then((arrayBuffer) => audioCtx.decodeAudioData(arrayBuffer.slice(0)))
      .then((decoded) => {
        if (cancelled) return;

        const channel = decoded.getChannelData(0);
        const samplesPerBar = Math.max(
          1,
          Math.floor(channel.length / STATIC_BAR_COUNT),
        );
        const peaks = new Array<number>(STATIC_BAR_COUNT);

        let max = 0;
        for (let i = 0; i < STATIC_BAR_COUNT; i += 1) {
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, channel.length);
          let sumSquares = 0;
          for (let j = start; j < end; j += 1) {
            sumSquares += channel[j] * channel[j];
          }
          const rms = Math.sqrt(sumSquares / (end - start));
          peaks[i] = rms;
          if (rms > max) max = rms;
        }

        // Normalize peak to 1.0 so quiet recordings still show shape.
        if (max > 0) {
          for (let i = 0; i < peaks.length; i += 1) {
            peaks[i] = peaks[i] / max;
          }
        }
        setBars(peaks);
      })
      .catch(() => {
        if (cancelled) return;
        // Decode failure: flat bars; <audio> playback still works.
        setBars(new Array<number>(STATIC_BAR_COUNT).fill(0.1));
      })
      .finally(() => {
        void audioCtx.close();
      });

    return () => {
      cancelled = true;
    };
  }, [blob]);

  return bars;
};
